import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, AlertTriangle } from "lucide-react";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";

const PAIN_POINTS = [
  "CLIA citations for missing or outdated SOPs",
  "Competency assessment documentation gaps",
  "CAP checklist items unresolved at inspection time",
  "Documentation backlog from lean staffing",
  "Risk from using generic AI without compliance guardrails",
];

const CAPABILITIES = [
  { label: "CAPA Documentation", href: "/solutions/capa-documentation" },
  { label: "SOP Management", href: "/solutions/sop-management" },
  { label: "Inspection Readiness", href: "/solutions/inspection-readiness" },
  { label: "Deviation Documentation", href: "/solutions/deviation-documentation" },
];

const BLOG_LINKS = [
  { label: "CLIA Gap Audit Guide", href: "/blog/clia-gap-audit" },
  { label: "CAP Inspection Readiness", href: "/blog/cap-inspection-readiness" },
  { label: "Lab SOP Management", href: "/blog/lab-sop-management" },
];

export default function ClinicalDiagnosticLabs() {
  return (
    <>
      <SEO
        title="CLIA & CAP Compliance for Clinical and Diagnostic Labs | LabComplyx"
        description="LabComplyx helps clinical and diagnostic labs achieve CLIA compliance, CAP inspection readiness, and audit-ready documentation with governed workflows and human review."
        canonical="https://labcomplyx.com/industries/clinical-diagnostic"
      />
      <div className="pt-24">
        {/* Hero */}
        <section className="pb-16 bg-background">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-2xl">
              <span className="inline-block text-xs font-semibold text-secondary bg-secondary/10 px-3 py-1 rounded-full mb-4">CLIA · CAP</span>
              <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-5 leading-tight">
                CLIA & CAP compliance for clinical labs under inspection pressure
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                LabComplyx helps clinical and diagnostic labs reduce documentation backlog, improve competency records, and produce audit-ready outputs — without the overhead of a large consulting engagement.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/ValidationIntake">
                  <Button size="lg" className="gap-2 w-full sm:w-auto">
                    Book a Free CLIA Gap Audit <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/solutions/inspection-readiness">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto">Inspection Readiness</Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Pain points */}
        <section className="py-16 bg-muted/30 border-y border-border">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-6">Common challenges in clinical lab compliance</h2>
                <ul className="space-y-3">
                  {PAIN_POINTS.map((p) => (
                    <li key={p} className="flex items-start gap-3 text-sm text-muted-foreground">
                      <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-card border border-border rounded-xl p-7">
                <h3 className="font-semibold text-foreground mb-4">How LabComplyx helps clinical labs</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                  We provide governed, AI-assisted documentation workflows specifically aligned to CLIA and CAP requirements. Every output is human-reviewed before entering your quality record — no unsupported assertions, no audit exposure.
                </p>
                <ul className="space-y-2.5">
                  {["CLIA-aligned SOP templates and drafting workflows", "CAP checklist-mapped inspection readiness tools", "Competency assessment documentation support", "Traceable CAPA and deviation records", "Audit trail captured at every step"].map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                      <Check className="w-4 h-4 text-secondary shrink-0 mt-0.5" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Not a LIMS statement */}
        <section className="py-10 bg-background border-b border-border">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="bg-muted/40 border border-border rounded-xl p-5 max-w-2xl">
              <p className="text-sm text-muted-foreground leading-relaxed">
                <strong className="text-foreground">LabComplyx is not a LIS or full eQMS replacement.</strong> It is a governed documentation and QA-support layer that works alongside your current laboratory information system and quality management processes — reducing documentation backlog and inspection risk without requiring a platform change.
              </p>
            </div>
          </div>
        </section>

        {/* State licensure notice */}
        <section className="py-10 bg-background border-b border-border">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="bg-amber-50/50 border border-amber-200 rounded-xl p-5 max-w-2xl">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" aria-hidden="true" />
                <h2 className="text-base font-semibold text-foreground">State Laboratory Licensure Requirements</h2>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Clinical diagnostic laboratories operating in or sending specimens to certain states may be subject to additional state licensure requirements beyond CLIA certification. As of 2024, approximately 23 states maintain independent laboratory licensure programs — including New York (CLEP), California (CDPH), Florida, Maryland, Pennsylvania, and others — with requirements that may differ from federal CLIA standards. LabComplyx compliance documentation does not substitute for legal or regulatory counsel on state licensure applicability. Laboratories should verify current state requirements directly with applicable state health departments.
              </p>
            </div>
          </div>
        </section>

        {/* Related capabilities */}
        <section className="py-16 bg-background">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <h2 className="text-xl font-bold text-foreground mb-6">Related capabilities</h2>
            <div className="flex flex-wrap gap-3 mb-10">
              {CAPABILITIES.map((c) => (
                <Link key={c.href} to={c.href} className="inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-lg border border-border bg-card hover:border-primary/40 hover:text-primary transition-colors">
                  {c.label} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              ))}
            </div>
            <h3 className="text-base font-semibold text-foreground mb-4">Relevant guides & resources</h3>
            <div className="flex flex-wrap gap-3">
              {BLOG_LINKS.map((b) => (
                <Link key={b.href} to={b.href} className="inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-lg border border-border bg-muted/30 hover:border-primary/40 hover:text-primary transition-colors">
                  {b.label} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-primary text-primary-foreground">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-2xl font-bold mb-4">Ready to reduce your CLIA documentation risk?</h2>
            <p className="text-primary-foreground/80 mb-8 leading-relaxed">Book a free CLIA Gap Audit. We'll identify the top documentation gaps in your lab and outline a clear path to inspection readiness.</p>
            <Link to="/ValidationIntake">
              <Button size="lg" variant="secondary" className="gap-2">
                Book Your Free CLIA Gap Audit <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}